
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ActivityLog, Language } from '../types';
import { t } from '../locales';
import { Footprints, Clock, Zap, Thermometer, X, Edit2, CheckCircle2 } from 'lucide-react';

interface ActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  logs: ActivityLog[];
  onSave: (log: ActivityLog) => void;
  onUpdate: (id: string, log: ActivityLog) => void;
  onDelete: (id: string) => void;
  language: Language;
}

const ActivityModal: React.FC<ActivityModalProps> = ({ 
    isOpen, onClose, logs, onSave, onUpdate, onDelete, language 
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activityType, setActivityType] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(30);
  const [calories, setCalories] = useState(200);
  const [temperature, setTemperature] = useState<number | undefined>();
  const [isFetchingTemp, setIsFetchingTemp] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Initialize form with defaults or current time
  const resetForm = () => {
    const now = new Date();
    setActivityType('');
    setStartTime(`${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`);
    setDuration(30);
    setCalories(200);
    setEditingId(null);
    fetchLocalTemperature();
  };

  useEffect(() => {
    if (isOpen && !editingId) {
        resetForm();
    }
  }, [isOpen]);

  const fetchLocalTemperature = async () => {
      if (!navigator.geolocation || editingId) return; // Don't auto-fetch if editing
      
      setIsFetchingTemp(true);
      navigator.geolocation.getCurrentPosition(async (pos) => {
          try {
              const { latitude, longitude } = pos.coords;
              const res = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current_weather=true`);
              const data = await res.json();
              if (data && data.current_weather) {
                  setTemperature(data.current_weather.temperature);
              }
          } catch (e) {
              console.warn("Could not fetch temperature", e);
          } finally {
              setIsFetchingTemp(false);
          }
      }, (err) => {
          console.warn("Geolocation denied", err);
          setIsFetchingTemp(false);
      });
  };

  const handleAction = () => {
      if (!activityType.trim()) return;
      
      const logData: ActivityLog = {
          id: editingId || crypto.randomUUID(),
          type: activityType,
          startTime,
          duration,
          calories,
          temperature,
          timestamp: editingId ? (logs.find(l => l.id === editingId)?.timestamp || new Date()) : new Date()
      };

      if (editingId) {
          onUpdate(editingId, logData);
      } else {
          onSave(logData);
      }
      
      // Visual feedback
      setShowFeedback(true);
      setTimeout(() => setShowFeedback(false), 2000);
      
      resetForm();
      // NOTE: Modal stays open as per user request.
  };

  const startEdit = (log: ActivityLog) => {
      setEditingId(log.id);
      setActivityType(log.type);
      setStartTime(log.startTime);
      setDuration(log.duration);
      setCalories(log.calories);
      setTemperature(log.temperature);
      // Scroll form into view
      const form = document.getElementById('activity-form-container');
      form?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!isOpen) return null;

  const todaysLogs = logs.filter(l => new Date(l.timestamp).toDateString() === new Date().toDateString());

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-50 dark:bg-gray-900 animate-slideIn">
      <div className="w-full h-full max-w-md mx-auto flex flex-col bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
        
        {/* Header */}
        <div className="px-4 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-blue-50/50 dark:bg-gray-900/80 backdrop-blur-md sticky top-0 z-10 pt-safe">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Footprints size={24} color="#3b82f6" fill="#3b82f6" fillOpacity={0.2} />
                {editingId ? t(language, 'update') : t(language, 'sportInfo')}
            </h2>
            <button 
                onClick={onClose}
                className="p-2 rounded-full bg-white dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700 shadow-sm border border-gray-100 dark:border-gray-700"
            >
                <X size={24} />
            </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-6 min-h-0 relative">
            
            {/* Entry Form */}
            <div id="activity-form-container" className={`bg-blue-50/30 dark:bg-blue-900/10 p-5 rounded-2xl border transition-all mb-8 space-y-4 ${editingId ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md' : 'border-blue-100 dark:border-blue-800/30'}`}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">
                        {editingId ? 'Editing Entry' : 'New Activity'}
                    </span>
                    {editingId && (
                        <button onClick={resetForm} className="text-[10px] font-bold text-gray-400 hover:text-red-500 uppercase tracking-widest">Cancel Edit</button>
                    )}
                </div>
                
                <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'activityType')}</label>
                    <input 
                        type="text" 
                        value={activityType}
                        onChange={(e) => setActivityType(e.target.value)}
                        placeholder="e.g. Running, Yoga..."
                        className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none font-medium"
                    />
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                            <Clock size={10} /> {t(language, 'startTime')}
                        </label>
                        <input 
                            type="time" 
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                            <Zap size={10} /> {t(language, 'duration')}
                        </label>
                        <input 
                            type="number" 
                            value={duration}
                            onChange={(e) => setDuration(parseInt(e.target.value) || 0)}
                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none font-bold"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">{t(language, 'caloriesBurned')}</label>
                        <input 
                            type="number" 
                            value={calories}
                            onChange={(e) => setCalories(parseInt(e.target.value) || 0)}
                            className="w-full p-3 rounded-xl border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white outline-none font-bold"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                            <Thermometer size={10} /> {t(language, 'currentTemperature')}
                        </label>
                        <div className="w-full p-3 rounded-xl border border-gray-100 dark:border-gray-800 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center font-bold text-blue-600 dark:text-blue-400 h-[48px]">
                            {isFetchingTemp ? (
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                                temperature !== undefined ? `${temperature}°C` : t(language, 'noTemp')
                            )}
                        </div>
                    </div>
                </div>

                <button 
                    onClick={handleAction}
                    disabled={!activityType.trim()}
                    className={`w-full py-4 font-bold rounded-xl shadow-lg transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${editingId ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-600 hover:bg-blue-500'} text-white`}
                >
                    {showFeedback ? (
                        <>
                            <CheckCircle2 size={20} />
                            {t(language, 'recordedSuccessfully')}
                        </>
                    ) : (
                        editingId ? t(language, 'update') : t(language, 'logActivity')
                    )}
                </button>
            </div>

            {/* History List */}
            <div className="pb-10">
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    {t(language, 'activitiesLogged')}
                </h3>
                <div className="space-y-3">
                    {todaysLogs.length === 0 ? (
                        <p className="text-center text-gray-400 text-sm italic py-4">No activities logged today.</p>
                    ) : (
                        todaysLogs.map(log => (
                            <div 
                                key={log.id} 
                                onClick={() => startEdit(log)}
                                className={`bg-white dark:bg-gray-800 border p-4 rounded-2xl flex justify-between items-center shadow-sm cursor-pointer transition-all hover:bg-blue-50/50 dark:hover:bg-blue-900/10 active:scale-[0.98] ${editingId === log.id ? 'border-blue-500 ring-1 ring-blue-500 bg-blue-50/30' : 'border-gray-100 dark:border-gray-700'}`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-blue-600">
                                        <Footprints size={18} color="#3b82f6" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 dark:text-white capitalize flex items-center gap-2">
                                            {log.type}
                                            <Edit2 size={10} className="text-gray-300" />
                                        </h4>
                                        <div className="flex gap-2 text-[10px] text-gray-500 dark:text-gray-400 font-medium">
                                            <span>{log.startTime}</span>
                                            <span>•</span>
                                            <span>{log.duration} min</span>
                                            {log.temperature !== undefined && (
                                                <>
                                                    <span>•</span>
                                                    <span className="text-blue-500">{log.temperature}°C</span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <span className="block font-bold text-gray-800 dark:text-white text-sm">{log.calories}</span>
                                        <span className="text-[8px] text-gray-400 uppercase font-bold">kcal</span>
                                    </div>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); onDelete(log.id); if(editingId === log.id) resetForm(); }} 
                                        className="p-2 text-gray-300 hover:text-red-500 transition-colors"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActivityModal;
